import { useState, useEffect } from "react"
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react"
import { useAuth } from "../contexts/AuthProvider"
import OrgScheduleInterviewForm from "./OrgScheduleInterviewForm"
import interviewTrackingService from "../services/interviewTrackingService"
import CalendarLoader from "./CalendarLoader"

const CalendarScheduling = ({
  organizationData,
  interviewersData,
  allInterviews,
}) => {
  const { user } = useAuth()
  const [organization, setOrganization] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [interviews, setInterviews] = useState(allInterviews)
  const [isShowingScheduleForm, setIsShowingScheduleForm] = useState(false)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [currentDate, setCurrentDate] = useState(new Date())
  const [dragStart, setDragStart] = useState(null)
  const [dragEnd, setDragEnd] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [orgMembers, setOrgMembers] = useState([])
  const [showExtendedHours, setShowExtendedHours] = useState({
    before: false,
    after: false,
  })
  const [viewMode, setViewMode] = useState("workweek") // "week" or "workweek"
  const [showViewDropdown, setShowViewDropdown] = useState(false)

  useEffect(() => {
    const setupCalendar = async () => {
      if (!user?.uid || !organizationData || !interviewersData?.length) return

      setIsLoading(true)

      try {
        setOrganization(organizationData)
        setOrgMembers(interviewersData)

        const isAdminResult = organizationData.adminUid === user.uid
        setIsAdmin(isAdminResult)

        const processedInterviews = interviews.map((interview) => {
          if (interview.rawDate instanceof Date) {
            return interview
          }

          if (
            interview.scheduledDate &&
            typeof interview.scheduledDate === "object" &&
            interview.scheduledDate.seconds
          ) {
            const scheduledDate = new Date(
              interview.scheduledDate.seconds * 1000
            )
            return { ...interview, rawDate: scheduledDate }
          }

          if (interview.scheduledDate) {
            const scheduledDate =
              interview.scheduledDate instanceof Date
                ? interview.scheduledDate
                : new Date(interview.scheduledDate)
            return { ...interview, rawDate: scheduledDate }
          }

          if (interview.date && interview.time) {
            try {
              const dateParts = new Date(interview.date)
              const timeParts = interview.time.match(/(\d+):(\d+) (\w+)/)

              if (timeParts) {
                let hours = parseInt(timeParts[1])
                const minutes = parseInt(timeParts[2])
                const ampm = timeParts[3].toUpperCase()

                if (ampm === "PM" && hours < 12) hours += 12
                if (ampm === "AM" && hours === 12) hours = 0

                dateParts.setHours(hours, minutes, 0, 0)
                return { ...interview, rawDate: dateParts }
              }
            } catch (err) {
              console.error(
                "Error parsing date/time:",
                err,
                interview.date,
                interview.time
              )
            }
          }

          return interview
        })

        setInterviews(processedInterviews || [])
      } catch (error) {
        console.error("Error setting up calendar:", error)
      } finally {
        setIsLoading(false)
      }
    }

    setupCalendar()
  }, [])

  const goToToday = () => {
    setCurrentDate(new Date())
  }

  const goToPrev = () => {
    const prevWeek = new Date(currentDate)
    prevWeek.setDate(currentDate.getDate() - 7)
    setCurrentDate(prevWeek)
  }

  const goToNext = () => {
    const nextWeek = new Date(currentDate)
    nextWeek.setDate(currentDate.getDate() + 7)
    setCurrentDate(nextWeek)
  }

  const getWeekDates = () => {
    const dates = []
    const firstDay = new Date(currentDate)

    // Find the Sunday of the current week
    const day = currentDate.getDay()
    const diff = currentDate.getDate() - day // Sunday is 0
    firstDay.setDate(diff)

    if (viewMode === "workweek") {
      // Generate 5 days (Monday to Friday)
      for (let i = 1; i <= 5; i++) {
        const date = new Date(firstDay)
        date.setDate(firstDay.getDate() + i)
        dates.push(date)
      }
    } else {
      // Generate 7 days (Sunday to Saturday)
      for (let i = 0; i < 7; i++) {
        const date = new Date(firstDay)
        date.setDate(firstDay.getDate() + i)
        dates.push(date)
      }
    }

    return dates
  }

  const formatDateRange = () => {
    const weekDates = getWeekDates()
    const firstDate = weekDates[0]
    const lastDate = weekDates[weekDates.length - 1]

    if (firstDate.getMonth() !== lastDate.getMonth()) {
      return `${firstDate.getDate()} ${firstDate.toLocaleDateString("en-US", {
        month: "short",
      })}–${lastDate.getDate()} ${lastDate.toLocaleDateString("en-US", {
        month: "short",
      })}, ${lastDate.getFullYear()}`
    }

    return `${firstDate.getDate()}–${lastDate.getDate()} ${firstDate.toLocaleDateString(
      "en-US",
      { month: "short" }
    )}, ${firstDate.getFullYear()}`
  }

  // Get time slots - default 9AM to 9PM, expandable to full 24 hours
  const getTimeSlots = () => {
    const slots = []

    // Determine start and end hours based on expansion state
    let startHour = showExtendedHours.before ? 0 : 9
    let endHour = showExtendedHours.after ? 24 : 21

    for (let hour = startHour; hour < endHour; hour++) {
      slots.push({ hour, minutes: 0 })
      slots.push({ hour, minutes: 30 })
    }
    return slots
  }

  const formatTimeSlot = (slot) => {
    const hour = slot.hour % 12 === 0 ? 12 : slot.hour % 12
    const period = slot.hour >= 12 ? "PM" : "AM"
    return `${hour} ${period}`
  }

  const handleDragStart = (date, timeSlot) => {
    if (!isAdmin) return

    const selectedDateTime = new Date(date)
    selectedDateTime.setHours(timeSlot.hour, timeSlot.minutes, 0, 0)

    setDragStart(selectedDateTime)
    setIsDragging(true)
  }

  const handleDragEnd = (date, timeSlot) => {
    if (!isAdmin || !isDragging) return

    const selectedDateTime = new Date(date)
    selectedDateTime.setHours(timeSlot.hour, timeSlot.minutes, 0, 0)

    selectedDateTime.setMinutes(selectedDateTime.getMinutes() + 30)

    setDragEnd(selectedDateTime)
    setIsDragging(false)

    processDragSelection()
  }

  const handleDragOver = (date, timeSlot) => {
    if (!isAdmin || !isDragging) return

    const selectedDateTime = new Date(date)
    selectedDateTime.setHours(timeSlot.hour, timeSlot.minutes, 0, 0)

    setDragEnd(selectedDateTime)
  }

  const processDragSelection = () => {
    if (!dragStart || !dragEnd) return

    let startTime = new Date(dragStart)
    let endTime = new Date(dragEnd)

    // Swap if dragged backwards
    if (startTime > endTime) {
      ;[startTime, endTime] = [endTime, startTime]
    }

    // Ensure endTime includes the full 30-minute slot
    // Add 30 minutes to the end time to capture the complete slot
    endTime.setMinutes(endTime.getMinutes() + 30)

    const formattedDate = startTime.toISOString().split("T")[0]

    const formattedStartTime = `${String(startTime.getHours()).padStart(
      2,
      "0"
    )}:${String(startTime.getMinutes()).padStart(2, "0")}`

    const formattedEndTime = `${String(endTime.getHours()).padStart(
      2,
      "0"
    )}:${String(endTime.getMinutes()).padStart(2, "0")}`

    setSelectedSlot({
      date: startTime,
      formattedDate,
      formattedTime: formattedStartTime,
      formattedEndTime,
    })

    setIsShowingScheduleForm(true)

    // Reset drag state
    setDragStart(null)
    setDragEnd(null)
  }

  const isInDragSelection = (date, timeSlot) => {
    if (!isDragging || !dragStart || !dragEnd) return false

    const slotDateTime = new Date(date)
    slotDateTime.setHours(timeSlot.hour, timeSlot.minutes, 0, 0)

    const startTime = dragStart < dragEnd ? dragStart : dragEnd
    const endTime = dragStart < dragEnd ? dragEnd : dragStart

    return slotDateTime >= startTime && slotDateTime <= endTime
  }

  const getInterviewsForTimeSlot = (date, timeSlot) => {
    const slotStartTime = new Date(date)
    slotStartTime.setHours(timeSlot.hour, timeSlot.minutes, 0, 0)

    const slotEndTime = new Date(slotStartTime)
    slotEndTime.setMinutes(slotStartTime.getMinutes() + 30)

    return interviews.filter((interview) => {
      if (
        !interview.rawDate &&
        !interview.scheduledDate &&
        !(interview.date && interview.time)
      ) {
        return false
      }

      let interviewDate

      if (interview.rawDate instanceof Date) {
        interviewDate = interview.rawDate
      } else if (interview.scheduledDate) {
        try {
          interviewDate =
            interview.scheduledDate instanceof Date
              ? interview.scheduledDate
              : new Date(interview.scheduledDate)
        } catch (err) {
          console.error("Error converting scheduledDate:", err)
        }
      } else if (interview.date && interview.time) {
        try {
          const dateParts = new Date(interview.date)
          if (isNaN(dateParts.getTime())) {
            return false
          }

          const timeParts = interview.time.match(/(\d+):(\d+) (\w+)/)

          if (timeParts) {
            let hours = parseInt(timeParts[1])
            const minutes = parseInt(timeParts[2])
            const ampm = timeParts[3].toUpperCase()

            if (ampm === "PM" && hours < 12) hours += 12
            if (ampm === "AM" && hours === 12) hours = 0

            dateParts.setHours(hours, minutes, 0, 0)
            interviewDate = dateParts
          }
        } catch (err) {
          console.error(
            "Error parsing date/time:",
            err,
            interview.date,
            interview.time
          )
          return false
        }
      }

      if (!interviewDate || isNaN(interviewDate.getTime())) {
        return false
      }

      // Check if interview falls within this 30-minute slot
      const isSameDay =
        interviewDate.getFullYear() === slotStartTime.getFullYear() &&
        interviewDate.getMonth() === slotStartTime.getMonth() &&
        interviewDate.getDate() === slotStartTime.getDate()

      if (!isSameDay) {
        return false
      }

      // Calculate interview end time (default 1 hour if duration not specified)
      const interviewDuration = interview.duration || 60 // duration in minutes
      const interviewEndTime = new Date(interviewDate)
      interviewEndTime.setMinutes(
        interviewDate.getMinutes() + interviewDuration
      )

      // Check if this slot overlaps with the interview time range
      // Slot overlaps if: interview starts before slot ends AND interview ends after slot starts
      return interviewDate < slotEndTime && interviewEndTime > slotStartTime
    })
  }

  const handleScheduleComplete = async (meetingId) => {
    try {
      if (organization) {
        setIsLoading(true)
        const allInterviews =
          await interviewTrackingService.getOrganizationInterviews(
            organization.id
          )
        const scheduledInterviews = allInterviews.filter(
          (interview) => interview.status === "scheduled"
        )

        const processedInterviews = scheduledInterviews.map((interview) => {
          if (interview.rawDate instanceof Date) {
            return interview
          }

          if (
            interview.scheduledDate &&
            typeof interview.scheduledDate === "object" &&
            interview.scheduledDate.seconds
          ) {
            const scheduledDate = new Date(
              interview.scheduledDate.seconds * 1000
            )
            return { ...interview, rawDate: scheduledDate }
          }

          if (interview.scheduledDate) {
            const scheduledDate =
              interview.scheduledDate instanceof Date
                ? interview.scheduledDate
                : new Date(interview.scheduledDate)
            return { ...interview, rawDate: scheduledDate }
          }

          return interview
        })

        setInterviews(processedInterviews || [])
        setIsLoading(false)
      }
    } catch (error) {
      console.error("Error refreshing interviews:", error)
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return <CalendarLoader />
  }

  if (!organization) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">
          No organization found. Please create or join an organization first.
        </p>
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">
          Only organization admins can schedule interviews.
        </p>
      </div>
    )
  }

  const weekDates = getWeekDates()
  const timeSlots = getTimeSlots()
  const numDays = weekDates.length

  return (
    <div className="bg-white border-l border-r">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={goToToday}
              className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 font-medium"
            >
              Today
            </button>

            <div className="flex items-center">
              <button
                onClick={goToPrev}
                className="p-1 rounded-md hover:bg-gray-100 text-gray-600"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={goToNext}
                className="p-1 rounded-md hover:bg-gray-100 text-gray-600"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <h2 className="text-lg font-semibold text-gray-800">
              {formatDateRange()}
            </h2>
          </div>

          {/* View Mode Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowViewDropdown(!showViewDropdown)}
              className="flex items-center space-x-2 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 font-medium"
            >
              <span>{viewMode === "workweek" ? "Work Week" : "Week"}</span>
              <ChevronDown size={16} />
            </button>

            {showViewDropdown && (
              <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                <button
                  onClick={() => {
                    setViewMode("workweek")
                    setShowViewDropdown(false)
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                    viewMode === "workweek"
                      ? "bg-blue-50 text-blue-600 font-medium"
                      : "text-gray-700"
                  }`}
                >
                  Work Week
                </button>
                <button
                  onClick={() => {
                    setViewMode("week")
                    setShowViewDropdown(false)
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                    viewMode === "week"
                      ? "bg-blue-50 text-blue-600 font-medium"
                      : "text-gray-700"
                  }`}
                >
                  Week
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="overflow-auto">
        <div className="min-w-[800px]">
          <div
            className="border-b border-gray-200"
            style={{
              display: "grid",
              gridTemplateColumns: `60px repeat(${numDays}, 1fr)`,
            }}
          >
            <div className="p-2 text-center border-r border-gray-200"></div>
            {weekDates.map((date, index) => {
              const isToday = date.toDateString() === new Date().toDateString()
              const dayNumber = date.getDate()
              const dayName = date.toLocaleDateString("en-US", {
                weekday: "short",
              })
              return (
                <div
                  key={index}
                  className={`p-3 text-center border-r border-gray-200 ${
                    isToday ? "bg-blue-50" : ""
                  }`}
                >
                  <div
                    className={`text-xl font-medium ${
                      isToday ? "text-blue-600" : "text-gray-800"
                    }`}
                  >
                    {dayNumber}
                  </div>
                  <div className="text-sm text-gray-500">{dayName}</div>
                </div>
              )
            })}
          </div>
          <div className="relative">
            {/* Show "earlier hours" button if not expanded, or collapse button if expanded */}
            {!showExtendedHours.before ? (
              <div
                className="border-b border-gray-200 bg-gray-50 hover:bg-gray-100 cursor-pointer"
                style={{
                  display: "grid",
                  gridTemplateColumns: `60px repeat(${numDays}, 1fr)`,
                }}
                onClick={() =>
                  setShowExtendedHours((prev) => ({ ...prev, before: true }))
                }
              >
                <div className="p-2 text-center border-r border-gray-200"></div>
                <div
                  className="p-2 text-center text-sm text-gray-600 border-r border-gray-200"
                  style={{ gridColumn: `2 / span ${numDays}` }}
                >
                  Show earlier hours (12 AM - 9 AM)
                </div>
              </div>
            ) : (
              <div
                className="border-b border-gray-200 bg-blue-50 hover:bg-blue-100 cursor-pointer"
                style={{
                  display: "grid",
                  gridTemplateColumns: `60px repeat(${numDays}, 1fr)`,
                }}
                onClick={() =>
                  setShowExtendedHours((prev) => ({ ...prev, before: false }))
                }
              >
                <div className="p-2 text-center border-r border-gray-200"></div>
                <div
                  className="p-2 text-center text-sm text-blue-600 font-medium border-r border-gray-200"
                  style={{ gridColumn: `2 / span ${numDays}` }}
                >
                  Hide earlier hours (12 AM - 9 AM)
                </div>
              </div>
            )}

            {timeSlots.map((timeSlot, slotIndex) => (
              <div
                key={slotIndex}
                className={`${
                  timeSlot.minutes === 0
                    ? "border-t border-gray-200"
                    : "border-t border-gray-200 border-dashed"
                }`}
                style={{
                  display: "grid",
                  gridTemplateColumns: `60px repeat(${numDays}, 1fr)`,
                }}
              >
                <div
                  className={`p-2 text-right pr-3 text-xs text-gray-500 border-r border-gray-200 ${
                    timeSlot.minutes === 0 ? "pt-3" : ""
                  }`}
                >
                  {timeSlot.minutes === 0 && formatTimeSlot(timeSlot)}
                </div>

                {weekDates.map((date, dateIndex) => {
                  const slotInterviews = getInterviewsForTimeSlot(
                    date,
                    timeSlot
                  )
                  const inDragSelection = isInDragSelection(date, timeSlot)

                  // Check if this is the first slot where the interview appears
                  const isFirstSlotForInterview = (interview) => {
                    if (!interview.rawDate && !interview.scheduledDate)
                      return false

                    let interviewDate =
                      interview.rawDate || interview.scheduledDate
                    if (!(interviewDate instanceof Date)) {
                      interviewDate = new Date(interviewDate)
                    }

                    const slotStartTime = new Date(date)
                    slotStartTime.setHours(
                      timeSlot.hour,
                      timeSlot.minutes,
                      0,
                      0
                    )

                    // Check if interview starts in this exact slot
                    return (
                      interviewDate.getHours() === slotStartTime.getHours() &&
                      interviewDate.getMinutes() ===
                        slotStartTime.getMinutes() &&
                      interviewDate.getDate() === slotStartTime.getDate()
                    )
                  }

                  // Calculate how many slots this interview spans
                  const getInterviewHeight = (interview) => {
                    const duration = interview.duration || 60 // default 1 hour
                    const slots = Math.ceil(duration / 30) // number of 30-min slots
                    return slots * 40 // 40px per slot (h-10)
                  }

                  return (
                    <div
                      key={dateIndex}
                      className={`h-10 border-r border-gray-200 relative 
      ${inDragSelection ? "bg-indigo-100 bg-opacity-60" : ""} 
      cursor-pointer transition-colors duration-100`}
                      onMouseDown={() => handleDragStart(date, timeSlot)}
                      onMouseUp={() => handleDragEnd(date, timeSlot)}
                      onMouseOver={() => handleDragOver(date, timeSlot)}
                    >
                      {slotInterviews.length > 0 &&
                        slotInterviews.map((interview, idx) => {
                          // Only render the interview block at its starting slot
                          if (!isFirstSlotForInterview(interview)) return null

                          const height = getInterviewHeight(interview)

                          return (
                            <div
                              key={idx}
                              className="absolute top-0 left-0 right-1 p-0.5 z-10"
                              style={{ height: `${height}px` }}
                            >
                              <div className="bg-indigo-100 border-l-4 border-indigo-600 rounded-sm px-2 py-1 h-full overflow-hidden shadow-sm">
                                <div className="text-xs font-medium text-indigo-800 truncate">
                                  {interview.title || "Interview"}
                                </div>
                                <div className="flex justify-between text-xs text-indigo-600">
                                  <div className="truncate pr-1">
                                    {interview.candidate ||
                                      interview.candidateName ||
                                      interview.candidateEmail ||
                                      "Candidate"}
                                  </div>
                                  <div className="truncate font-medium text-indigo-800 text-right">
                                    {interview.interviewer ||
                                      interview.interviewerName ||
                                      ""}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                    </div>
                  )
                })}
              </div>
            ))}

            {/* Show "later hours" button if not expanded, or collapse button if expanded */}
            {!showExtendedHours.after ? (
              <div
                className="border-t border-gray-200 bg-gray-50 hover:bg-gray-100 cursor-pointer"
                style={{
                  display: "grid",
                  gridTemplateColumns: `60px repeat(${numDays}, 1fr)`,
                }}
                onClick={() =>
                  setShowExtendedHours((prev) => ({ ...prev, after: true }))
                }
              >
                <div className="p-2 text-center border-r border-gray-200"></div>
                <div
                  className="p-2 text-center text-sm text-gray-600 border-r border-gray-200"
                  style={{ gridColumn: `2 / span ${numDays}` }}
                >
                  Show later hours (9 PM - 12 AM)
                </div>
              </div>
            ) : (
              <div
                className="border-t border-gray-200 bg-blue-50 hover:bg-blue-100 cursor-pointer"
                style={{
                  display: "grid",
                  gridTemplateColumns: `60px repeat(${numDays}, 1fr)`,
                }}
                onClick={() =>
                  setShowExtendedHours((prev) => ({ ...prev, after: false }))
                }
              >
                <div className="p-2 text-center border-r border-gray-200"></div>
                <div
                  className="p-2 text-center text-sm text-blue-600 font-medium border-r border-gray-200"
                  style={{ gridColumn: `2 / span ${numDays}` }}
                >
                  Hide later hours (9 PM - 12 AM)
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isShowingScheduleForm && (
        <OrgScheduleInterviewForm
          isOpen={isShowingScheduleForm}
          onClose={() => {
            setIsShowingScheduleForm(false)
            setSelectedSlot(null)
          }}
          interviewType={"schedule"}
          onScheduleComplete={handleScheduleComplete}
          selectedDate={selectedSlot?.formattedDate}
          selectedTime={selectedSlot?.formattedTime}
          selectedEndTime={selectedSlot?.formattedEndTime}
          organizationData={organization}
          interviewersData={orgMembers}
        />
      )}
    </div>
  )
}

export default CalendarScheduling